import {useTranslation} from 'react-i18next';

import CheckIcon from '@mui/icons-material/Check';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import SettingsIcon from '@mui/icons-material/Settings';
import {Grid, SxProps} from '@mui/material';

import Clipboard from './Clipboard';
import {SurveyEmailTemplate} from './SurveyEmailTemplate';
import {EmpionButton} from '../../../../../../../components/EmpionButton/EmpionButton';
import {EmpionTooltip} from '../../../../../../../components/EmpionTooltip/EmpionTooltip';
import {greenColors, orangeColors} from '../../../../../../../layouts/theme/designSystem';
import {Survey} from '../../../types/SurveyTypes';

interface Props {
  state: string | null;
  survey: Survey | undefined;
  allowComplete?: boolean;
  onConfigure?(): void;
  onComplete?(): void;
  onDiscard?(): void;
}

export const SurveyButtons = ({state, survey, allowComplete = false, onConfigure, onComplete, onDiscard}: Props) => {
  const {t} = useTranslation();

  // Only renders for visible current/upcoming survey cards.
  if (state !== 'current' && state !== 'upcoming') return null;

  const containerStyles: SxProps = {
    display: 'flex',
    alignItems: 'flex-start',
    gap: {xs: '6px', sm: '12px', md: '16px'},
    marginTop: {xs: '6px', sm: '10px', md: '12px'},
  };

  const btnStyles: SxProps = {
    backgroundColor: orangeColors[7],
    color: greenColors[700],
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: orangeColors[4],
    },
  };

  return (
    <Grid container sx={containerStyles}>
      {onConfigure && (
        <EmpionButton variant='primary' onClick={onConfigure} startIcon={<SettingsIcon fontSize='small' />}>
          {t('companySurveys.actionButtons.show')}
        </EmpionButton>
      )}
      <Clipboard text={t('companySurveys.actionButtons.copySurveyLink')} styles={btnStyles} survey={survey} />
      <SurveyEmailTemplate btnStyles={btnStyles} />
      {onDiscard && (
        <EmpionButton onClick={onDiscard} variant='warning' startIcon={<DeleteForeverIcon fontSize='small' />}>
          {t('companySurveys.actionButtons.discardSurvey')}
        </EmpionButton>
      )}
      {onComplete && (
        <EmpionTooltip title={t('companySurveys.actionButtons.blockClosingSurveyTooltip')} isEnabled={!allowComplete}>
          <span>
            <EmpionButton
              onClick={onComplete}
              disabled={!allowComplete}
              variant='cta'
              startIcon={<CheckIcon fontSize='small' />}
            >
              {t('companySurveys.actionButtons.complete')}
            </EmpionButton>
          </span>
        </EmpionTooltip>
      )}
    </Grid>
  );
};
